import DateDayMonthInput from '@components/DateInput/DateDayMonthInput'
import DateInput from '@components/DateInput/DateInput'
import DatetimeInput from '@components/DateInput/DatetimeInput'
import CountryDropdown from '@components/Dropdown/CountryDropdown'
import Dropdown from '@components/Dropdown/Dropdown'
import { isArray, isEmptyString, isNullOrUndefined } from '@utils/types'
import { SortOrder } from 'primereact/api'
import { IconField } from 'primereact/iconfield'
import { InputIcon } from 'primereact/inputicon'
import { InputNumber } from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'
import { MultiSelect } from 'primereact/multiselect'
import { useEffect, useRef, useState } from 'react'

// Internal to this component only — not shared, to avoid colliding with utils/constants/input.js's own INT_MAX_VALUE
const DEBOUNCE_VALUE = 20
const FILTER_MAX_VALUE = Number.MAX_SAFE_INTEGER

/**
 * NewFilters - Generic filter component for table headers
 *
 * This component provides a reusable header with sorting and filtering capabilities
 * that can be used in tables or lists. It was extracted from ListLayout's customHeader
 * to be used as a standalone component.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.field - Field name used to populate the column
 * @param {string} [props.sortField] - If defined, field used to sort this column (defaults to field)
 * @param {string} props.header - Label of the column header
 * @param {boolean} [props.sortable] - Indicates if the column is sortable
 * @param {boolean} [props.filterable=true] - If false, doesn't show filter for the column
 * @param {Array} [props.options] - If defined, uses a dropdown for filter instead of input text
 * @param {string} [props.type] - Filter input type (component displayName), e.g. Dropdown, MultiSelect, InputNumber
 * @param {string} [props.currentSortField] - Current sort field from parent component
 * @param {number} [props.currentSortOrder] - Current sort order from parent component (SortOrder.ASC or SortOrder.DESC)
 * @param {Function} [props.setSortField] - Callback to set the sort field in parent component
 * @param {Function} [props.setSortOrder] - Callback to set the sort order in parent component
 * @param {Function} [props.orderingCallback] - Optional callback executed when ordering changes
 * @param {boolean} [props.showFilters=false] - Whether to display the filter inputs
 * @param {Object} [props.filters={}] - Current filters object from parent component
 * @param {Function} [props.setFilters] - Callback to update filters in parent component
 * @returns {JSX.Element} The filter component with header and optional filter input
 *
 */
export default function Filters({
  field,
  sortField = null,
  header,
  sortable,
  filterable = true,
  options,
  type,
  currentSortField,
  currentSortOrder,
  setSortField,
  setSortOrder,
  orderingCallback = null,
  showFilters = false,
  filters = {},
  setFilters
}) {
  const [localValue, setLocalValue] = useState(
    isArray(filters?.[field]) ? filters?.[field][0] : filters?.[field]
  )
  const debounceTimerRef = useRef(null)
  const filtersRef = useRef(filters)
  const setFiltersRef = useRef(setFilters)

  useEffect(() => {
    filtersRef.current = filters
  }, [filters])

  useEffect(() => {
    setFiltersRef.current = setFilters
  }, [setFilters])

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    debounceTimerRef.current = setTimeout(() => {
      let newFilters = { ...filtersRef.current }
      if (!isNullOrUndefined(localValue) && !isEmptyString(localValue)) {
        newFilters[field] = [localValue]
      } else {
        delete newFilters[field]
      }

      setFiltersRef?.current && setFiltersRef.current(newFilters)
    }, DEBOUNCE_VALUE)

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [localValue, field, filtersRef])

  const sortableField = sortField ?? field

  const dateInputOptions = [
    DateInput.displayName,
    DatetimeInput.displayName,
    DateDayMonthInput.displayName
  ]

  /**
   * Toggles the sort order for this column (or sets it to ascending for a new column) and
   * notifies the parent component via setSortField/setSortOrder, plus orderingCallback if provided.
   *
   * @returns {void}
   */
  const handleSort = () => {
    const order = currentSortField === sortableField ? -currentSortOrder : SortOrder.ASC

    setSortField(sortableField)
    setSortOrder(order)

    if (orderingCallback) {
      orderingCallback(sortableField, order)
    }
  }

  /**
   * Builds the CSS class list for the header container, based on whether the column is sortable
   * and currently sorted.
   *
   * @returns {string} Space-separated class names for the header.
   */
  const getClassNames = () => {
    const classNames = ['custom-header-content']
    if (sortable) {
      classNames.push('header-sortable')
      if (currentSortField === sortableField) {
        classNames.push('header-sorted')
      }
    }
    return classNames.join(' ')
  }

  /**
   * Updates the parent component's filters immediately, without debounce — used for dropdowns,
   * date inputs, and InputNumber, where change events are already discrete.
   *
   * @param {Object} e - Change event (PrimeReact or native), exposing `.value` or `.target.value`.
   * @returns {void}
   */
  const updateFilterImmediate = e => {
    let newFilters = { ...filters }
    const value = e.target?.value ?? e.value
    if (!isNullOrUndefined(value) && !isEmptyString(value)) {
      newFilters[field] = isArray(value) ? value : [value]
    } else {
      delete newFilters[field]
    }
    setFilters(newFilters)
  }

  /**
   * Updates local input state immediately for a responsive UI; the debounced effect above syncs
   * the value to the parent component's filters after DEBOUNCE_VALUE ms.
   *
   * @param {Object} e - Change event (native input), exposing `.value` or `.target.value`.
   * @returns {void}
   */
  const updateFilter = e => {
    const value = e.target?.value ?? e.value
    setLocalValue(value)
  }

  return (
    <div className="custom-header-container">
      <div
        className={getClassNames()}
        onClick={sortable ? handleSort : null}
      >
        <span className="custom-header-label">{header}</span>

        {sortable && ( // if sortable, we show icon for sorting
          <span className="custom-header-icon">
            {currentSortField === sortableField && currentSortOrder === SortOrder.ASC && (
              <i className="fa-solid fa-sort-up" />
            )}
            {currentSortField === sortableField && currentSortOrder === SortOrder.DESC && (
              <i className="fa-solid fa-sort-down" />
            )}
            {currentSortField !== sortableField && <i className="fa-solid fa-sort" />}
          </span>
        )}
      </div>

      {showFilters && filterable && (
        <div className="custom-filters">
          {type === Dropdown.displayName ? (
            <Dropdown
              value={isArray(filters?.[field]) ? filters?.[field][0] : filters?.[field]}
              options={options.options}
              optionLabel={options.optionLabel}
              optionValue={options.optionValue}
              onChange={options.onChange ? options.onChange : updateFilterImmediate}
              placeholder={header}
            />
          ) : type === MultiSelect.displayName ? (
            <MultiSelect
              value={
                isArray(filters?.[field]) ? filters[field] : [filters?.[field]].filter(Boolean)
              }
              options={options.options}
              optionLabel={options.optionLabel}
              optionValue={options.optionValue}
              onChange={e => updateFilterImmediate({ value: e.value?.length ? e.value : null })}
              filter
              showClear
              maxSelectedLabels={1}
              placeholder={header}
            />
          ) : type === CountryDropdown.displayName ? (
            <CountryDropdown
              value={isArray(filters?.[field]) ? filters?.[field][0] : filters?.[field]}
              onChange={updateFilterImmediate}
              showClear={true}
            />
          ) : dateInputOptions.includes(type) ? (
            <div className="custom-filters-datetime">
              <DateInput
                value={filters?.[field] ?? ''}
                onChange={updateFilterImmediate}
                placeholder="Date"
              />
            </div>
          ) : type === InputNumber.displayName ? (
            <IconField className="custom-filter-input">
              <InputNumber
                value={filters?.[field] ?? null}
                onChange={updateFilterImmediate}
                showButtons={options?.showButtons ?? false}
                min={options?.min ?? 0}
                max={options?.max ?? FILTER_MAX_VALUE}
                minFractionDigits={options?.minFractionDigits ?? 0}
              />
            </IconField>
          ) : (
            <IconField className="custom-filter-input">
              <InputIcon className="fa-solid fa-magnifying-glass" />
              <InputText
                value={localValue ?? ''}
                onChange={updateFilter}
              />
            </IconField>
          )}
        </div>
      )}
    </div>
  )
}
